<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-width="600px"
    overlay-opacity="0.85"
    content-class="modern-agency-dialog"
  >
    <v-card class="rounded-xl" elevation="16">
      <!-- Header with gradient background -->
      <v-card-title
        class="editor-header d-flex justify-space-between align-center px-6 py-4"
      >
        <div class="text-h5 font-weight-bold white--text">
          <v-icon large color="white" class="mr-3">mdi-trophy</v-icon>
          Edit Team Challenge
        </div>
        <v-btn
          icon
          @click="onClose"
          class="close-btn"
          color="white"
          depressed
          large
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider></v-divider>

      <!-- Help text -->
      <v-card-text class="px-6 pt-5 pb-1 text-body-1 text--secondary">
        <div class="d-flex align-center">
          <v-icon color="primary" class="mr-2">mdi-information-outline</v-icon>
          Edit the Team Challenge and invite agencies
        </div>
      </v-card-text>

      <!-- Error/Success message with smooth transition -->
      <v-expand-transition>
        <v-card-text class="px-6 py-0" v-if="message">
          <v-alert
            :type="messageType"
            class="mb-4 elevation-2"
            transition="scale-transition"
            :icon="
              messageType === 'success'
                ? 'mdi-check-circle'
                : 'mdi-alert-circle'
            "
          >
            {{ message }}
          </v-alert>
        </v-card-text>
      </v-expand-transition>

      <!-- Main content -->
      <v-card-text class="px-6 py-2" style="max-height: 50vh; overflow-y: auto">
        <v-row>
          <v-col cols="12" class="py-2">
            <v-text-field
              v-model="competition.title"
              label="Title*"
              outlined
              dense
              prepend-inner-icon="mdi-format-title"
              :rules="[rules.required]"
              class="modern-input"
            />
          </v-col>

          <v-col cols="12" class="py-2">
            <v-textarea
              v-model="competition.description"
              label="Description*"
              outlined
              dense
              rows="4"
              auto-grow
              prepend-inner-icon="mdi-text"
              :rules="[rules.required]"
              class="modern-input"
            />
          </v-col>

            <v-col cols="12" md="6" class="py-2">
            <v-menu
              v-model="start_date_picker"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
              <v-text-field
              v-model="formattedStartDate"
              label="Start Date*"
              outlined
              dense
              prepend-inner-icon="mdi-calendar"
              readonly
              v-bind="attrs"
              v-on="on"
              :rules="[rules.required]"
              class="modern-input"
              ></v-text-field>
              </template>
              <v-date-picker
              v-model="start_date"
              @input="saveStartDate"
              :min="
              new Date(new Date().setDate(new Date().getDate() + 1))
              .toISOString()
              .substr(0, 10)
              "
              ></v-date-picker>
            </v-menu>
            </v-col>

            <v-col cols="12" md="6" class="py-2">
            <v-menu
              v-model="end_date_picker"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
              <v-text-field
              v-model="formattedEndDate"
              label="End Date*"
              outlined
              dense
              prepend-inner-icon="mdi-calendar"
              readonly
              v-bind="attrs"
              v-on="on"
              :rules="[rules.required]"
              class="modern-input"
              ></v-text-field>
              </template>
              <v-date-picker
              v-model="end_date"
              @input="saveEndDate"
              :min="
              new Date(new Date().setDate(new Date().getDate() + 1))
              .toISOString()
              .substr(0, 10)
              "
              ></v-date-picker>
            </v-menu>
            </v-col>

          <v-col cols="12" class="py-2">
            <v-select
              v-model="competition.requested_agency_ids"
              :items="agencyList"
              item-text="name"
              item-value="_id"
              label="Select Agencies*"
              outlined
              dense
              multiple
              prepend-inner-icon="mdi-office-building-marker"
              :rules="[rules.required]"
              disabled
              class="modern-input"
            >
              <template v-slot:prepend-item>
                <v-list-item
                  ripple
                  @click="selectAllAgencies"
                  v-if="agencyList.length > 0"
                >
                  <v-list-item-action>
                    <v-checkbox
                      :input-value="allSelected"
                      color="primary"
                    ></v-checkbox>
                  </v-list-item-action>
                  <v-list-item-content>
                    <v-list-item-title>Select All</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </template>
            </v-select>
          </v-col>

          <v-col cols="12" class="py-2">
            <v-text-field
              v-model="competition.target_points"
              label="Target Points*"
              outlined
              dense
              type="number"
              min="1"
              prepend-inner-icon="mdi-numeric"
              :rules="[rules.required]"
              @input="onTarget"
              class="modern-input"
            />
          </v-col>
        </v-row>
      </v-card-text>

      <!-- Action buttons -->
      <v-card-actions class="px-6 pb-6 pt-4 d-flex justify-space-between">
        <v-btn text @click="onClose" color="grey darken-1" class="px-6" large>
          <v-icon left>mdi-arrow-left</v-icon>
          Cancel
        </v-btn>
        <v-btn
          color="primary"
          @click="onSave"
          depressed
          width="160"
          :loading="loading"
          class="px-6 save-btn"
          large
          dark
        >
          <template v-slot:loader>
            <span class="custom-loader">
              <v-icon light>mdi-autorenew</v-icon>
            </span>
          </template>
          <v-icon left>mdi-content-save</v-icon>
          Update
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapState } from "vuex";
export default {
  props: {
    dialog: {
      type: Boolean,
      default: false
    },
    onClose: {
      type: Function
    },
    respectiveCompetition: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      message: null,
      messageType: "error",
      loading: false,
      rules: {
        required: value => !!value || "This field is required"
      },
      competition: { ...this.respectiveCompetition },
      agency: {},
      agencyList: [],
      start_date_picker: false,
      end_date_picker: false,
      start_date: this.respectiveCompetition.start_date
        ? this.respectiveCompetition.start_date.split("T")[0]
        : "",
      end_date: this.respectiveCompetition.end_date
        ? this.respectiveCompetition.end_date.split("T")[0]
        : "",
      allSelected: false
    };
  },
  computed: {
    formattedStartDate() {
      return this.start_date;
    },
    formattedEndDate() {
      return this.end_date;
    },
    ...mapState("auth", {
      profile: "profile"
    })
  },
  watch: {
    respectiveCompetition: {
      handler(newVal) {
        this.competition = { ...newVal };
        this.start_date = newVal.start_date
          ? newVal.start_date.split("T")[0]
          : "";
        this.end_date = newVal.end_date ? newVal.end_date.split("T")[0] : "";
      },
      deep: true
    }
  },
  methods: {
    ...mapActions({
      updateCompetition: "competition/editCompetition",
      getCompetition: "competition/getCompetition",
      fetchEachCompetitions: "competition/fetchEachCompetitions"
    }),
    selectAllAgencies() {
      if (this.allSelected) {
        this.competition.requested_agency_ids = [];
      } else {
        this.competition.requested_agency_ids = this.agencyList.map(
          item => item._id
        );
      }
      this.allSelected = !this.allSelected;
    },
    onTarget(value) {
      const numericValue = value.replace(/[^0-9]/g, "");
      if (numericValue === "" || Number(numericValue) < 1) {
        this.competition.target_points = 1;
      } else {
        this.competition.target_points = Number(numericValue);
      }
    },
    updateAllSelected() {
      this.allSelected =
        this.competition.requested_agency_ids.length === this.agencyList.length;
    },
    saveStartDate(date) {
      this.start_date = date;
      this.competition.start_date = date;
      this.start_date_picker = false;
    },
    saveEndDate(date) {
      this.end_date = date;
      this.competition.end_date = date;
      this.end_date_picker = false;
    },
    async onSave() {
      this.message = null;
      
      if (!this.competition.title) {
        this.message = "Please enter a title";
        this.messageType = "error";
        return;
      }
      if (!this.competition.description) {
        this.message = "Please enter a description";
        this.messageType = "error";
        return;
      }
      if (!this.start_date) {
        this.message = "Please select a start date";
        this.messageType = "error";
        return;
      }
      if (!this.end_date) {
        this.message = "Please select an end date";
        this.messageType = "error";
        return;
      }
      if (!this.competition.target_points) {
        this.message = "Please enter target points";
        this.messageType = "error";
        return;
      }

      const params = {
        ...this.competition,
        competitionId: this.respectiveCompetition.competition_id,
        start_date: this.start_date,
        end_date: this.end_date,
        requested_agency_ids: this.competition.requested_agency_ids,
        agency_id: this.agency._id,
        status: this.competition.status || "Active",
        competition_status: this.competition.competition_status || "Ongoing"
      };

      this.loading = true;
      try {
        const response = await this.updateCompetition(params);
        this.competition = response;
        this.message = "Team challenge updated successfully!";
        this.messageType = "success";
        setTimeout(() => {
          this.onClose();
          this.loadCompetitions();
        }, 1500);
      } catch (error) {
        this.message = error|| "An error occurred";
        this.messageType = "error";
      } finally {
        this.loading = false;
      }
    },
    reset() {
      this.competition = { ...this.respectiveCompetition };
      this.start_date = this.respectiveCompetition.start_date
        ? this.respectiveCompetition.start_date.split("T")[0]
        : "";
      this.end_date = this.respectiveCompetition.end_date
        ? this.respectiveCompetition.end_date.split("T")[0]
        : "";
      this.message = null;
      this.allSelected = false;
    },
    async loadCompetitions() {
      try {
        const response = await this.fetchEachCompetitions();
        this.competitions = response;
      } catch (error) {
        this.message = error.message || "An error occurred while loading competitions.";
        this.messageType = "error";
      }
    },
    loadagencyList() {
      const agencyId = this.agency._id;
      this.getCompetition({ agencyId })
        .then(res => {
          this.agencyList = res;
          this.allSelected = false;
          this.loading = false;
        })
        .catch(error => {
          this.loading = false;
          this.message = error.response?.data?.message || "An error occurred";
          this.messageType = "error";
        });
    }
  },
  mounted() {
    this.agency = this.profile;
    this.loadagencyList();
    this.loadCompetitions();
  }
};
</script>

<style scoped>
.modern-agency-dialog {
  border-radius: 16px !important;
  overflow: hidden;
}

.editor-header {
     background: linear-gradient(135deg, #3b7ca1 0%, #084468 100%);
}

.close-btn {
  transition: all 0.3s ease;
}

.close-btn:hover {
  transform: rotate(90deg);
  background: rgba(255, 255, 255, 0.2) !important;
}

.modern-input::v-deep .v-input__slot {
  border-radius: 10px !important;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
  background-color: white !important;
}

.save-btn {
  box-shadow: 0 4px 12px rgba(58, 123, 213, 0.3) !important;
  transition: all 0.3s ease;
}

.save-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(58, 123, 213, 0.4) !important;
}

.save-btn:active {
  transform: translateY(0);
}

.custom-loader {
  animation: loader 1s infinite;
  display: flex;
}

@keyframes loader {
  from {
    transform: rotate(0);
  }
  to {
    transform: rotate(360deg);
  }
}

/* Scrollbar styling */
.modern-agency-dialog::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.modern-agency-dialog::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

.modern-agency-dialog::-webkit-scrollbar-thumb {
     background: linear-gradient(135deg, #3b7ca1 0%, #084468 100%);
  border-radius: 10px;
}

.modern-agency-dialog::-webkit-scrollbar-thumb:hover {
  background: #3a7bd5;
}
</style>